import React, { useMemo, useState, useEffect, useContext } from "react";
import {
  FiShuffle,
  FiExternalLink,
  FiCheck,
  FiRepeat,
  FiBookmark,
  FiFileText,
  FiPause,
  FiPlay,
  FiRotateCcw,
} from "react-icons/fi";
import { ThemeContext } from "../../App";
import { slugify } from "../../utils/slug";
import "./practice.css";

const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const DIFFICULTY_PILL = { Easy: "green", Medium: "yellow", Hard: "red", "": "gray" };
const DIFFICULTY_LABEL = { Easy: "Easy", Medium: "Medium", Hard: "Hard", "": "No level" };

function fmt(secs) {
  const m = Math.floor(secs / 60)
    .toString()
    .padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function Practice({ questionData, updateData }) {
  const dark = useContext(ThemeContext);

  // ---- filters (multi-select; empty array = "all") ----
  const [topics, setTopics] = useState([]); // selected topic names
  const [diffs, setDiffs] = useState([]); // selected difficulties
  const [bookmark, setBookmark] = useState("any"); // any | yes | no
  const [status, setStatus] = useState("unsolved"); // any | unsolved | solved | revise

  // ---- current pick + stopwatch ----
  const [current, setCurrent] = useState(null); // { ti, pi }
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  const allTopics = useMemo(
    () => (questionData || []).map((t) => t.topicName),
    [questionData]
  );

  // eligible pool from the active filters
  const pool = useMemo(() => {
    const out = [];
    (questionData || []).forEach((topic, ti) => {
      if (topics.length && !topics.includes(topic.topicName)) return;
      (topic.questions || []).forEach((q, pi) => {
        const d = q.Difficulty || "";
        if (diffs.length && !diffs.includes(d)) return;
        if (bookmark === "yes" && !q.Bookmark) return;
        if (bookmark === "no" && q.Bookmark) return;
        if (status === "unsolved" && q.Done) return;
        if (status === "solved" && !q.Done) return;
        if (status === "revise" && !q.Revise) return;
        out.push({ ti, pi });
      });
    });
    return out;
  }, [questionData, topics, diffs, bookmark, status]);

  // live problem object for the current pick
  const problem =
    current &&
    questionData[current.ti] &&
    questionData[current.ti].questions[current.pi]
      ? {
          ...questionData[current.ti].questions[current.pi],
          topicName: questionData[current.ti].topicName,
        }
      : null;

  // stopwatch tick
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  function pick() {
    if (pool.length === 0) return;
    let next = pool[Math.floor(Math.random() * pool.length)];
    if (pool.length > 1 && current) {
      let guard = 0;
      while (next.ti === current.ti && next.pi === current.pi && guard < 25) {
        next = pool[Math.floor(Math.random() * pool.length)];
        guard++;
      }
    }
    setCurrent(next);
    setElapsed(0);
    setRunning(true);
    setNotesOpen(false);
  }

  function toggleIn(list, setList, value) {
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  }

  // patch the current problem and persist (local + cloud) via the shared updateData
  function patchCurrent(patch) {
    if (!current) return;
    const topic = questionData[current.ti];
    const questions = topic.questions.map((q, i) =>
      i === current.pi ? { ...q, ...patch } : q
    );
    const done = questions.filter((q) => q.Done).length;
    updateData(
      slugify(topic.topicName),
      { started: done > 0, doneQuestions: done, questions },
      current.ti
    );
  }

  function toggleDone() {
    if (!problem) return;
    const next = !problem.Done;
    patchCurrent({
      Done: next,
      SolvedAt: next ? problem.SolvedAt || new Date().toISOString() : problem.SolvedAt,
      TimeSpent: next && elapsed > 0 ? elapsed : problem.TimeSpent || 0,
    });
    if (next) setRunning(false);
  }
  function toggleRevise() {
    if (problem) patchCurrent({ Revise: !problem.Revise });
  }
  function toggleBookmark() {
    if (problem) patchCurrent({ Bookmark: !problem.Bookmark });
  }
  function openNotes() {
    if (!problem) return;
    setNotesDraft(problem.Notes || "");
    setNotesOpen(true);
  }
  function saveNotes() {
    patchCurrent({ Notes: notesDraft.trim() });
    setNotesOpen(false);
  }

  const diff = problem ? problem.Difficulty || "" : "";

  return (
    <div className={`practice-page ${dark ? "practice-dark" : ""}`}>
      <div className="page-header">
        <div className="page-emoji" role="img" aria-label="dice">
          🎲
        </div>
        <h1 className="page-title">Practice</h1>
        <p className="page-sub">
          Filter by topic, difficulty, bookmark and status, then get one random
          problem to solve — time yourself and mark it done, for revision or
          bookmarked right here.
        </p>
      </div>

      {/* FILTERS */}
      <div className="pr-filters">
        <div className="pr-frow">
          <div className="pr-flabel">
            Topics
            {topics.length ? <span className="pr-fcount">{topics.length}</span> : <span className="pr-fall">All</span>}
          </div>
          <div className="pr-chips">
            {allTopics.map((name) => (
              <button
                key={name}
                type="button"
                className={`pr-chip ${topics.includes(name) ? "active" : ""}`}
                onClick={() => toggleIn(topics, setTopics, name)}
              >
                {name}
              </button>
            ))}
            {topics.length ? (
              <button type="button" className="pr-chip clear" onClick={() => setTopics([])}>
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <div className="pr-frow">
          <div className="pr-flabel">
            Difficulty
            {diffs.length ? <span className="pr-fcount">{diffs.length}</span> : <span className="pr-fall">All</span>}
          </div>
          <div className="pr-chips">
            {DIFFICULTIES.map((d) => (
              <button
                key={d}
                type="button"
                className={`pr-chip ${diffs.includes(d) ? "active" : ""}`}
                onClick={() => toggleIn(diffs, setDiffs, d)}
              >
                {d}
              </button>
            ))}
            <button
              type="button"
              className={`pr-chip ${diffs.includes("") ? "active" : ""}`}
              onClick={() => toggleIn(diffs, setDiffs, "")}
            >
              No level
            </button>
          </div>
        </div>

        <div className="nb-note">
          Heads up: not every problem has an official difficulty yet. Filtering by{" "}
          <b>Easy / Medium / Hard</b> hides the untagged ones — include them with{" "}
          <b>No level</b>, or simply leave difficulty on <b>All</b>.
        </div>

        <div className="pr-frow">
          <div className="pr-flabel">Bookmark</div>
          <div className="pr-seg">
            {[
              ["any", "Any"],
              ["yes", "Bookmarked"],
              ["no", "Not bookmarked"],
            ].map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`pr-segbtn ${bookmark === val ? "active" : ""}`}
                onClick={() => setBookmark(val)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="pr-frow">
          <div className="pr-flabel">Status</div>
          <div className="pr-seg">
            {[
              ["any", "Any"],
              ["unsolved", "Unsolved"],
              ["solved", "Solved"],
              ["revise", "To revise"],
            ].map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                className={`pr-segbtn ${status === val ? "active" : ""}`}
                onClick={() => setStatus(val)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* PICK BAR */}
      <div className="pr-pickbar">
        <button
          type="button"
          className="nb-btn nb-btn-primary pr-pick"
          onClick={pick}
          disabled={pool.length === 0}
        >
          <FiShuffle size={16} /> {current ? "Pick another" : "Pick random problem"}
        </button>
        <span className="pr-poolcount">
          {pool.length === 0
            ? "No problems match these filters"
            : `Pulling from ${pool.length} problem${pool.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {/* PROBLEM CARD */}
      {problem ? (
        <div className="pr-card">
          <div className="pr-card-top">
            <span className="nb-pill blue">{problem.topicName}</span>
            <span className={`nb-pill ${DIFFICULTY_PILL[diff]}`}>{DIFFICULTY_LABEL[diff]}</span>
            {problem.Done ? <span className="nb-pill green">Solved</span> : null}
            {problem.Revise ? <span className="nb-pill orange">To revise</span> : null}
            {problem.Bookmark ? <span className="nb-pill purple">Bookmarked</span> : null}
          </div>

          <div className="pr-card-name">{problem.Problem}</div>

          <div className="pr-timer">
            <span className="pr-time">{fmt(elapsed)}</span>
            <button
              type="button"
              className="nb-btn nb-btn-sm"
              onClick={() => setRunning((r) => !r)}
              title={running ? "Pause timer" : "Start timer"}
            >
              {running ? <FiPause size={14} /> : <FiPlay size={14} />}
              {running ? "Pause" : "Start"}
            </button>
            <button
              type="button"
              className="nb-btn nb-btn-sm"
              onClick={() => {
                setElapsed(0);
                setRunning(true);
              }}
              title="Reset timer"
            >
              <FiRotateCcw size={14} /> Reset
            </button>
          </div>

          <div className="pr-actions">
            {problem.URL ? (
              <a
                className="nb-btn nb-btn-primary"
                href={problem.URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FiExternalLink size={15} /> Open problem
              </a>
            ) : null}
            <button
              type="button"
              className={`nb-btn ${problem.Done ? "pr-on-green" : ""}`}
              onClick={toggleDone}
            >
              <FiCheck size={15} /> {problem.Done ? "Solved" : "Mark complete"}
            </button>
            <button
              type="button"
              className={`nb-btn ${problem.Revise ? "pr-on-orange" : ""}`}
              onClick={toggleRevise}
            >
              <FiRepeat size={15} /> {problem.Revise ? "Marked revise" : "Mark for revision"}
            </button>
            <button
              type="button"
              className={`nb-btn ${problem.Bookmark ? "pr-on-purple" : ""}`}
              onClick={toggleBookmark}
            >
              <FiBookmark size={15} /> {problem.Bookmark ? "Bookmarked" : "Bookmark"}
            </button>
            <button type="button" className="nb-btn" onClick={openNotes}>
              <FiFileText size={15} /> {problem.Notes && problem.Notes.length ? "Edit notes" : "Add notes"}
            </button>
          </div>

          {notesOpen ? (
            <div className="pr-notes">
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Your approach, edge cases, complexity…"
                rows={4}
              />
              <div className="pr-notes-actions">
                <button type="button" className="nb-btn nb-btn-sm" onClick={() => setNotesOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="nb-btn nb-btn-primary nb-btn-sm" onClick={saveNotes}>
                  Save notes
                </button>
              </div>
            </div>
          ) : problem.Notes && problem.Notes.length ? (
            <div className="pr-notes-preview">{problem.Notes}</div>
          ) : null}
        </div>
      ) : (
        <div className="pr-empty">
          <div className="pr-empty-emoji">🎯</div>
          <p>Set your filters and hit <strong>Pick random problem</strong> to start a focused practice rep.</p>
        </div>
      )}
    </div>
  );
}
